import { IconTextBadge } from '../components/ui/IconTextBadge/IconTextBadge';
import { useMemo, useState } from 'react';
import { Select, MenuItem, FormControl } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LessonBox } from '../components/ui/LessonBox/LessonBox';
import useCurrentUser from '@/api/useCurrentUser';
import { useLessons } from '@/api/useLessons';
import { useClasses } from '@/api/useClasses';
import { useSubjects } from '@/api/useSubjects';
import { useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider';

const selectStyles = {
  backgroundColor: 'white',
  borderRadius: '100px',
  height: '48px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiSelect-select': {
    paddingLeft: '24px',
    fontSize: '14px'
  },
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
};

const menuItemStyles = {
  fontSize: '14px'
};

const HomePage = () => {
  const [classValue, setClassValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');
  const { data: currentUser } = useCurrentUser();
  const { data: lessons = [], isLoading } = useLessons();
  const { data: classes = [] } = useClasses();
  const { data: subjects = [] } = useSubjects(classValue || undefined);
  const { searchQuery } = useDefaultLayoutContext();

  const filteredLessons = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return lessons.filter(l => {
      if (classValue && l.class_id !== classValue) return false;
      if (subjectValue && l.subject_id !== subjectValue) return false;
      if (query && !l.title.toLowerCase().includes(query) && !(l.subject ?? '').toLowerCase().includes(query)) return false;
      return true;
    });
  }, [lessons, classValue, subjectValue, searchQuery]);

  return (
    <div className="flex flex-col gap-6 pb-24">
      <div>
        <div className="flex gap-4 mb-8">
          <IconTextBadge icon="🔥" text="15 days" variant="orange" />
          <IconTextBadge icon="🎓" text="Name of the school and class" variant="blue" />
        </div>
        <h1 className="text-base font-semibold text-text-secondary">Hi {currentUser?.name || 'there'},</h1>
        <h2 className="text-3xl font-bold ">Keep learning!</h2>
      </div>

      <div className="flex gap-4">
        <FormControl fullWidth>
          <Select
            value={classValue}
            onChange={(e) => { setClassValue(e.target.value); setSubjectValue(''); }}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles}
          >
            <MenuItem value="" sx={menuItemStyles}>
              All Classes
            </MenuItem>
            {classes.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={menuItemStyles}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <Select
            value={subjectValue}
            onChange={(e) => setSubjectValue(e.target.value)}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles}
          >
            <MenuItem value="" sx={menuItemStyles}>
              All Subjects
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id} sx={menuItemStyles}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#4E7DF6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredLessons.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400 text-sm">No lessons found</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex mt-6 gap-4 min-w-min">
            {filteredLessons.map((lesson) => (
              <LessonBox
                key={lesson.id}
                id={lesson.id}
                title={lesson.title}
                subject={lesson.subject ?? ''}
                date={new Date(lesson.created_at).toLocaleDateString()}
                videoUrl={lesson.video_url}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
